import { normalizaAlias } from '../normalizacion/normalizaAlias.js'
import { resolverValorDetallado } from '../motor/parser.js'

const TABLA_RE = /\$\{tabla:([^\}]+)\}/g
const CELDA_RE = /\$\{celda:([^\}]+)\}/g
const CONTADOR_RE = /\$\{contador:([^\}]+)\}/g
const PLACEHOLDER_RE = /\$\{([^}]+)\}/g

function stripWrapper(str) {
  let s = (str || '').trim()
  if (s.startsWith('${') && s.endsWith('}')) s = s.slice(2, -1).trim()
  return s
}

function splitArgs(str) {
  const args = []
  let current = ''
  let inQuotes = false
  let escape = false
  let parenDepth = 0

  for (let i = 0; i < str.length; i++) {
    const c = str[i]
    if (escape) {
      current += c
      escape = false
      continue
    }
    if (c === '\\' && inQuotes) {
      current += c
      escape = true
      continue
    }
    if (c === '"') {
      current += c
      inQuotes = !inQuotes
      continue
    }
    if (!inQuotes && c === '(') {
      parenDepth++
      current += c
      continue
    }
    if (!inQuotes && c === ')') {
      parenDepth = Math.max(0, parenDepth - 1)
      current += c
      continue
    }
    if (!inQuotes && parenDepth === 0 && c === ',') {
      args.push(current.trim())
      current = ''
      continue
    }
    current += c
  }
  if (current.trim().length > 0) args.push(current.trim())
  return args
}

function listRows(xml) {
  const rows = []
  let pos = 0
  while (true) {
    const start = xml.indexOf('<w:tr', pos)
    if (start === -1) break
    const end = xml.indexOf('</w:tr>', start)
    if (end === -1) break
    const endPos = end + '</w:tr>'.length
    rows.push({ start, end: endPos, xml: xml.slice(start, endPos) })
    pos = endPos
  }
  return rows
}

function resolveTablaAlias(contenido, aliasToExpresion) {
  const key = contenido.trim()
  const exp = aliasToExpresion[key]?.expresion
  if (!exp) return null
  const raw = stripWrapper(exp)
  if (raw.startsWith('tabla:') || raw.startsWith('celda:') || raw.startsWith('contador:')) {
    return raw
  }
  return null
}

function expandAliasesForTabla(rowXml, aliasToExpresion) {
  return rowXml.replace(PLACEHOLDER_RE, (match, contenido) => {
    const exp = resolveTablaAlias(contenido, aliasToExpresion)
    if (!exp) return match
    return '${' + exp + '}'
  })
}

function parseTablaArgs(raw) {
  const args = splitArgs(raw)
  const nombre = (args[0] || '').trim()
  const nodo = (args[1] || '').trim()
  const keep = (args[2] || '').trim()
  return { nombre, nodo, keep }
}

function parseCeldaArgs(raw) {
  const args = splitArgs(raw)
  const tabla = (args[0] || '').trim()
  const campo = (args[1] || '').trim()
  const expr = args[2] != null ? args.slice(2).join(',').trim() : ''
  return { tabla, campo, expr }
}

function parseContadorArgs(raw) {
  const args = splitArgs(raw)
  const tabla = (args[0] || '').trim()
  const inicio = (args[1] || '').trim()
  const paso = (args[2] || '').trim()
  return { tabla, inicio, paso }
}

function normalizePathArg(arg, aliasToVariable, aliasToExpresion) {
  if (!arg) return ''
  let limpio = arg.trim()
  limpio = limpio.replace(/\[\*\]$/, '').replace(/\[\]$/, '')

  if (aliasToVariable[limpio]?.fullPath) return aliasToVariable[limpio].fullPath
  if (aliasToExpresion[limpio]?.expresion) {
    const raw = stripWrapper(aliasToExpresion[limpio].expresion)
    const m = raw.match(/^(v:|i:)(.+)$/)
    if (m) return m[2].trim()
  }
  const m = limpio.match(/^(v:|i:)(.+)$/)
  if (m) return m[2].trim()
  return limpio
}

function pathWithIndex(basePath, idx) {
  if (!basePath) return ''
  if (basePath.includes('[*]')) return basePath.replace('[*]', `[${idx}]`)
  if (basePath.includes('[]')) return basePath.replace('[]', `[${idx}]`)
  return `${basePath}[${idx}]`
}

function joinPath(base, campo) {
  if (!campo) return base
  if (campo.startsWith('[')) return `${base}${campo}`
  return `${base}.${campo}`
}

function relativizarCampo(fullPath, basePath) {
  if (!fullPath) return ''
  if (!basePath) return fullPath

  if (fullPath === basePath) return ''
  if (fullPath.startsWith(basePath + '.')) {
    let rest = fullPath.slice(basePath.length + 1)
    rest = rest.replace(/^\d+\.?/, '')
    return rest
  }
  if (fullPath.startsWith(basePath + '[')) {
    let rest = fullPath.slice(basePath.length)
    rest = rest.replace(/^\[\d+\]\.?/, '')
    if (rest.startsWith('.')) rest = rest.slice(1)
    return rest
  }
  return fullPath
}

function resolveCampoArg(campo, basePath, aliasToVariable, aliasToExpresion) {
  if (!campo) return ''
  const raw = campo.trim()
  if (/^(v:|i:)/.test(raw)) return raw

  let fullPath = aliasToVariable[raw]?.fullPath
  if (!fullPath && aliasToExpresion[raw]?.expresion) {
    const expRaw = stripWrapper(aliasToExpresion[raw].expresion)
    const m = expRaw.match(/^(v:|i:)(.+)$/)
    if (m) fullPath = m[2].trim()
  }

  if (fullPath) return relativizarCampo(fullPath, basePath)
  return relativizarCampo(raw, basePath)
}

function replaceTokenOutsideQuotes(str, token, replacement) {
  if (!str || !token) return str
  let out = ''
  let inQuotes = false
  let escape = false
  for (let i = 0; i < str.length; i++) {
    const c = str[i]
    if (escape) {
      out += c
      escape = false
      continue
    }
    if (c === '\\' && inQuotes) {
      out += c
      escape = true
      continue
    }
    if (c === '"') {
      out += c
      inQuotes = !inQuotes
      continue
    }
    if (!inQuotes && str.startsWith(token, i)) {
      out += replacement
      i += token.length - 1
      continue
    }
    out += c
  }
  return out
}

function reemplazarCeldas(rowXml, tablaNombre, basePath, idx, aliasToVariable, aliasToExpresion) {
  const basePathIdx = pathWithIndex(basePath, idx)
  return rowXml.replace(CELDA_RE, (match, raw) => {
    const { tabla, campo, expr } = parseCeldaArgs(raw)
    if (!tabla || tabla !== tablaNombre) return match

    const campoResolvido = resolveCampoArg(campo, basePath, aliasToVariable, aliasToExpresion)
    const baseExpr = /^(v:|i:)/.test(campoResolvido)
      ? campoResolvido
      : `v:${joinPath(basePathIdx, campoResolvido)}`

    if (!expr) return '${' + baseExpr + '}'

    const exprRaw = stripWrapper(expr)
    const exprWithBase = replaceTokenOutsideQuotes(exprRaw, '@v', baseExpr)
    return '${' + exprWithBase + '}'
  })
}

function reemplazarContadores(rowXml, tablaNombre, idx) {
  return rowXml.replace(CONTADOR_RE, (match, raw) => {
    const { tabla, inicio, paso } = parseContadorArgs(raw)
    if (!tabla || tabla !== tablaNombre) return match
    return '${contador:"' + tabla + '",' + inicio + ',' + paso + ',' + idx + '}'
  })
}

export function expandirTablas(xml, datos, variables = [], expresiones = [], errors = []) {
  const { aliasToVariable, aliasToExpresion } = normalizaAlias(variables, expresiones)
  const rows = listRows(xml)
  if (!rows.length) return xml

  let out = ''
  let cursor = 0

  for (const row of rows) {
    if (cursor < row.start) {
      out += xml.slice(cursor, row.start)
      cursor = row.start
    }

    let rowXml = expandAliasesForTabla(row.xml, aliasToExpresion)

    const tablaMatch = rowXml.match(TABLA_RE)
    if (!tablaMatch) {
      out += row.xml
      cursor = row.end
      continue
    }

    TABLA_RE.lastIndex = 0
    const first = TABLA_RE.exec(rowXml)
    TABLA_RE.lastIndex = 0
    if (!first) {
      out += row.xml
      cursor = row.end
      continue
    }

    const tablaRaw = first[1]
    const { nombre, nodo, keep } = parseTablaArgs(tablaRaw)
    if (!nombre || !nodo) {
      errors.push({ code: 'tabla_args_invalid', message: 'Tabla sin args válidos', detail: tablaRaw })
      out += row.xml
      cursor = row.end
      continue
    }

    const basePath = normalizePathArg(nodo, aliasToVariable, aliasToExpresion)
    if (!basePath) {
      errors.push({ code: 'tabla_base_invalid', message: 'Nodo principal inválido', detail: nodo })
      out += row.xml
      cursor = row.end
      continue
    }

    const baseValue = resolverValorDetallado(datos, basePath)
    if (!baseValue.ok) {
      errors.push(baseValue.error)
      out += rowXml
      cursor = row.end
      continue
    }

    const filas = baseValue.missing ? [] : baseValue.value

    if (!Array.isArray(filas)) {
      errors.push({
        code: 'tabla_base_not_array',
        message: 'El nodo principal de tabla no resuelve a un array',
        detail: `${nombre}: ${basePath}`,
      })
      out += rowXml
      cursor = row.end
      continue
    }

    const arr = filas
    let repeticiones = arr.length

    if (repeticiones === 0) {
      if (String(keep) === '1') {
        repeticiones = 1
      }
      else {
        cursor = row.end
        continue
      }
    }

    let block = ''
    for (let i = 0; i < repeticiones; i++) {
      let pieza = rowXml.replace(TABLA_RE, '')
      pieza = reemplazarCeldas(pieza, nombre, basePath, i, aliasToVariable, aliasToExpresion)
      pieza = reemplazarContadores(pieza, nombre, i)
      block += pieza
    }

    out += block
    cursor = row.end
  }

  if (cursor < xml.length) out += xml.slice(cursor)
  return out
}
